import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './TalentsList.css';

const TalentsList = () => {
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTalent, setEditingTalent] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form state for adding/editing talents
  const [formData, setFormData] = useState({
    talentId: '',
    name: '',
    role: '',
    stage: '',
    status: '',
    seniority: '',
    availability: ''
  });

  // Fetch talents on component mount
  useEffect(() => {
    fetchTalents();
  }, []);

  // Fetch talents with optional search
  const fetchTalents = async (search = '') => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getTalents(search);
      setTalents(response.rows || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching talents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchTalents(searchQuery);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle add talent
  const handleAddTalent = async (e) => {
    e.preventDefault();
    try {
      await apiService.addTalent(formData);
      setFormData({
        talentId: '',
        name: '',
        role: '',
        stage: '',
        status: '',
        seniority: '',
        availability: ''
      });
      setShowAddForm(false);
      fetchTalents(searchQuery);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle edit talent
  const handleEditTalent = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateTalent(formData);
      setEditingTalent(null);
      setFormData({
        talentId: '',
        name: '',
        role: '',
        stage: '',
        status: '',
        seniority: '',
        availability: ''
      });
      fetchTalents(searchQuery);
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing a talent
  const startEdit = (talent) => {
    setEditingTalent(talent.ROWID);
    setFormData({
      talentId: talent.talentId || '',
      name: talent.name || '',
      role: talent.role || '',
      stage: talent.stage || '',
      status: talent.status || '',
      seniority: talent.seniority || '',
      availability: talent.availability || ''
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingTalent(null);
    setFormData({
      talentId: '',
      name: '',
      role: '',
      stage: '',
      status: '',
      seniority: '',
      availability: ''
    });
  };

  // Handle delete talent
  const handleDeleteTalent = async (rowId) => {
    if (window.confirm('Are you sure you want to delete this talent?')) {
      try {
        await apiService.deleteTalent(rowId);
        fetchTalents(searchQuery);
      } catch (err) {
        setError(err.message);
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading talents...</div>;
  }

  return (
    <div className="talents-container">
      <div className="talents-header">
        <h2>Talents Management</h2>
        
        {/* Search Form */}
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search talents..."
            className="search-input"
          />
          <button type="submit" className="search-btn">Search</button>
          <button 
            type="button" 
            onClick={() => {
              setSearchQuery('');
              fetchTalents();
            }}
            className="clear-btn"
          >
            Clear
          </button>
        </form>

        {/* Add Talent Button */}
        <button 
          onClick={() => setShowAddForm(true)}
          className="add-btn"
        >
          Add New Talent
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingTalent) && (
        <div className="form-modal">
          <div className="form-content">
            <h3>{editingTalent ? 'Edit Talent' : 'Add New Talent'}</h3>
            <form onSubmit={editingTalent ? handleEditTalent : handleAddTalent}>
              <div className="form-row">
                <input
                  type="text"
                  name="talentId"
                  value={formData.talentId}
                  onChange={handleInputChange}
                  placeholder="Talent ID"
                  required
                />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Name"
                  required
                />
              </div>
              
              <div className="form-row">
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  placeholder="Role"
                />
                <input
                  type="text"
                  name="stage"
                  value={formData.stage}
                  onChange={handleInputChange}
                  placeholder="Stage"
                />
              </div>
              
              <div className="form-row">
                <input
                  type="text"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  placeholder="Status"
                />
                <input
                  type="text"
                  name="seniority"
                  value={formData.seniority}
                  onChange={handleInputChange}
                  placeholder="Seniority"
                />
              </div>
              
              <div className="form-row">
                <input
                  type="text"
                  name="availability"
                  value={formData.availability}
                  onChange={handleInputChange}
                  placeholder="Availability"
                />
              </div>
              
              <div className="form-actions">
                <button type="submit" className="save-btn">
                  {editingTalent ? 'Update' : 'Add'} Talent
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowAddForm(false);
                    cancelEdit();
                  }}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Talents List */}
      <div className="talents-list">
        {talents.length === 0 ? (
          <div className="no-data">No talents found</div>
        ) : (
          <div className="talents-grid">
            {talents.map((talent) => (
              <div key={talent.ROWID} className="talent-card">
                <div className="talent-header">
                  <h4>{talent.name || 'Unnamed Talent'}</h4>
                  <div className="talent-actions">
                    <button 
                      onClick={() => startEdit(talent)}
                      className="edit-btn"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteTalent(talent.ROWID)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <div className="talent-details">
                  <div className="detail-item">
                    <strong>ID:</strong> {talent.talentId || 'N/A'}
                  </div>
                  <div className="detail-item">
                    <strong>Role:</strong> {talent.role || 'N/A'}
                  </div>
                  <div className="detail-item">
                    <strong>Stage:</strong> {talent.stage || 'N/A'}
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong> 
                    <span className={`status-badge ${talent.status?.toLowerCase()}`}>
                      {talent.status || 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Seniority:</strong> {talent.seniority || 'N/A'}
                  </div>
                  <div className="detail-item">
                    <strong>Availability:</strong> {talent.availability || 'N/A'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentsList;
